import { useState } from 'react';
import { scr } from '../lib/api';
import { pct } from '../lib/types';
import type { AdoptionResult, RevenueResult, SlaComplianceResult, VolumesResult } from '../lib/types';
import { useApiQuery } from '../lib/useApi';
import { ErrorNote, PageHeader, Spinner, StatCard } from '../ui/components';

/**
 * Cabinet dashboard — per specs/C14.md, "read-only, mobile-friendly". This
 * page is the one a minister actually opens on a phone between meetings, so
 * the grid below defaults to a single column and only widens on larger
 * viewports; there are no wide scrolling tables here (those live on the
 * league table / heatmap pages instead).
 */
export function Dashboard() {
  const [month, setMonth] = useState('');
  const qs = month ? `?month=${month}` : '';

  const slaQ = useApiQuery<SlaComplianceResult>(`/api/v1/analytics/kpis/sla-compliance${qs}`);
  const volumesQ = useApiQuery<VolumesResult>(`/api/v1/analytics/kpis/volumes${qs}`);
  const revenueQ = useApiQuery<RevenueResult>(`/api/v1/analytics/kpis/revenue${qs}`);
  const adoptionQ = useApiQuery<AdoptionResult>(`/api/v1/analytics/kpis/adoption${qs}`);

  const loading = slaQ.loading || volumesQ.loading || revenueQ.loading || adoptionQ.loading;
  const error = slaQ.error ?? volumesQ.error ?? revenueQ.error ?? adoptionQ.error;

  const byCurrency = revenueQ.data?.byCurrency ?? [];
  const totalRevenue = byCurrency.reduce((s, c) => s + c.amount, 0);
  const totalPayments = byCurrency.reduce((s, c) => s + c.payments, 0);
  const revenueLabel =
    byCurrency.length <= 1 ? scr(totalRevenue) : byCurrency.map((c) => `${c.currency} ${c.amount.toLocaleString()}`).join(' · ');

  const digitalCases = adoptionQ.data?.cases.digitalRatioPct ?? null;
  const digitalUsage = adoptionQ.data?.serviceUsage.digitalRatioPct ?? null;
  const compliance = slaQ.data?.compliancePct ?? null;

  const complianceTone = compliance == null ? 'default' : compliance >= 90 ? 'good' : compliance >= 75 ? 'warn' : 'bad';
  const usageTone = digitalUsage == null ? 'default' : digitalUsage >= 60 ? 'good' : digitalUsage >= 40 ? 'warn' : 'bad';
  const casesTone = digitalCases == null ? 'default' : digitalCases >= 60 ? 'good' : digitalCases >= 40 ? 'warn' : 'bad';

  return (
    <div>
      <PageHeader
        title="Cabinet dashboard"
        subtitle="Whole-of-government performance, read-only"
        actions={
          <input
            type="month"
            className="field-input w-40 !py-1.5 text-xs"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            aria-label="Filter by month"
          />
        }
      />

      {month ? (
        <button className="mb-4 text-xs font-semibold text-brand-600 underline" onClick={() => setMonth('')}>
          Showing {month} — clear to see all-time
        </button>
      ) : null}

      {error ? (
        <div className="mb-6">
          <ErrorNote message={error} />
        </div>
      ) : null}

      {loading ? (
        <Spinner label="Loading Cabinet dashboard…" />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            label="SLA compliance"
            value={pct(compliance)}
            hint={`${(slaQ.data?.nonBreached ?? 0).toLocaleString()} of ${(slaQ.data?.totalClosed ?? 0).toLocaleString()} closed cases on time`}
            tone={complianceTone}
          />
          <StatCard
            label="Service volume"
            value={(volumesQ.data?.total ?? 0).toLocaleString()}
            hint={
              (volumesQ.data?.byChannel ?? []).map((c) => `${c.channel.toLowerCase()} ${c.count.toLocaleString()}`).join(' · ') ||
              'No usage recorded'
            }
          />
          <StatCard
            label="Revenue collected"
            value={revenueLabel}
            hint={`${totalPayments.toLocaleString()} payment(s)`}
            tone="good"
          />
          <StatCard
            label="Digital adoption — service usage"
            value={pct(digitalUsage)}
            hint={`${(adoptionQ.data?.serviceUsage.digital ?? 0).toLocaleString()} digital vs ${(adoptionQ.data?.serviceUsage.counter ?? 0).toLocaleString()} counter`}
            tone={usageTone}
          />
          <StatCard
            label="Digital adoption — cases"
            value={pct(digitalCases)}
            hint={`${(adoptionQ.data?.cases.digital ?? 0).toLocaleString()} digital vs ${(adoptionQ.data?.cases.counter ?? 0).toLocaleString()} counter`}
            tone={casesTone}
          />
          <StatCard
            label="Cases closed"
            value={(slaQ.data?.totalClosed ?? 0).toLocaleString()}
            hint={`${(slaQ.data?.breached ?? 0).toLocaleString()} breached SLA`}
            tone={(slaQ.data?.breached ?? 0) > 0 ? 'warn' : 'default'}
          />
        </div>
      )}

      <p className="mt-6 text-xs text-slate-400">
        Whole-of-government totals across every reporting MDA{month ? ` for ${month}` : ''}. See the MDA league table for a
        per-ministry breakdown, or the district heatmap for geographic spread.
      </p>
    </div>
  );
}
